import React, { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import debounce from 'lodash/debounce'

import { SuggestionType } from 'src/types/search'

import { SuggesterFullWrapper } from './styles'
import Input from 'src/comps/Atom/Inputs/Input/Input'
import ButtonReset from 'src/comps/ButtonReset'
import IconLocation from 'client/icons/Location'
import IconArrowBack from 'client/icons/ArrowBack'
import { ButtonIcon } from 'src/comps/Button/styles'
import { isProperty, isGeo } from 'src/context/search/utils'
import { ErrorMessage } from '../../ErrorMessage'

interface SuggesterFullProps {
  suggestion?: SuggestionType | null
  onChangeInput: (value: string) => void
  onClear: () => void
  hasFocus?: boolean
  hasError?: boolean
  onClose: () => void
  onFocus?: () => void
}

const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <ButtonIcon onClick={onClick}>
    <IconArrowBack size={20} />
  </ButtonIcon>
)

export function SuggesterFull({
  suggestion,
  onChangeInput,
  onClear,
  hasFocus = false,
  hasError = false,
  onClose,
  onFocus,
}: SuggesterFullProps) {
  const intl = useIntl()
  const placeholderText = intl.formatMessage({ id: 'search.placeholder' })
  const mapAreaText = intl.formatMessage({ id: 'global.search-map-area' })
  const errorMessage = intl.formatMessage({ id: 'global.enter-destination' })

  const isGeoSuggestion = isGeo(suggestion?.Type ?? '')
  const suggestionText = isProperty(suggestion?.Type) ? suggestion?.Text : suggestion?.Description
  const initialSearchTerm = isGeoSuggestion ? mapAreaText : (suggestionText ?? '')

  const [searchTerm, setSearchTerm] = useState(initialSearchTerm)

  useEffect(() => {
    setSearchTerm(isGeoSuggestion ? mapAreaText : (suggestionText ?? ''))
  }, [suggestionText, mapAreaText, isGeoSuggestion])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const externalOnChangeInput = useCallback(
    debounce(
      data => {
        onChangeInput(data)
      },
      300,
      {
        leading: false,
        trailing: true,
      }
    ),
    [onChangeInput]
  )

  function handleChangeInput(text: string) {
    setSearchTerm(text)
    externalOnChangeInput(text)
  }

  function handleResetInput() {
    setSearchTerm('')
    externalOnChangeInput('')
    onClear()
  }

  return (
    <SuggesterFullWrapper
      className="suggesterFullWrapper"
      hasFocus={hasFocus}
      hasError={hasError}
      data-testid="suggester-full"
      onFocus={onFocus}
    >
      {hasFocus && <CloseButton onClick={onClose} />}
      <Input
        label=""
        icon={!hasFocus && <IconLocation size={24} />}
        onChange={handleChangeInput}
        value={searchTerm}
        placeholder={placeholderText}
      />
      {hasError && !searchTerm && <ErrorMessage errorMessage={errorMessage} />}
      <ButtonReset handleClick={handleResetInput} visible={searchTerm?.length > 0} />
    </SuggesterFullWrapper>
  )
}
